import React, { useCallback, useMemo, useRef, useState } from 'react'
import { closestCenter } from '@dnd-kit/core'
import type { CollisionDetection, DragStartEvent, DragMoveEvent, DragOverEvent, DragEndEvent } from '@dnd-kit/core'
import { verticalListSortingStrategy } from '@dnd-kit/sortable'
import { SECTION_ID_PREFIX, SECTION_GAP_PREFIX } from '../../../hooks/useSectionDrag'
import type { Task, Section } from '../../../../../lib/db/models'

interface RenderSegment {
  section: Section | null
  tasks: Task[]
  taskCount: number
}

interface UseDndCompositionParams {
  segments: RenderSegment[]
  excludeId: string | undefined
  // Section drag handlers
  handleSectionDragStart: (event: DragStartEvent) => boolean
  handleSectionDragOver: (event: DragOverEvent) => void
  handleSectionDragEnd: (event: DragEndEvent) => void
  handleSectionDragCancel: () => void
  activeSectionDragId: string | null
  // Task drag handlers
  handleDragStart: (event: DragStartEvent) => void
  handleDragMove: (event: DragMoveEvent) => void
  handleDragOver: (event: DragOverEvent) => void
  handleDragEnd: (event: DragEndEvent) => void
  handleDragCancel: () => void
  // Cross-segment gap direction ref (true = after, false = before, null = undetermined)
  crossSegGapAfterRef: React.RefObject<boolean | null>
  // State mirror of crossSegGapAfterRef — triggers re-render when direction flips
  crossSegGapAfter?: boolean | null
}

export function useDndComposition({
  segments,
  excludeId,
  handleSectionDragStart,
  handleSectionDragOver,
  handleSectionDragEnd,
  handleSectionDragCancel,
  activeSectionDragId,
  handleDragStart,
  handleDragOver,
  handleDragEnd,
  handleDragMove: taskDragMove,
  handleDragCancel,
  crossSegGapAfterRef,
  crossSegGapAfter,
}: UseDndCompositionParams) {
  // Width of the dragged section header — applied to the overlay so the cursor stays inside
  const [sectionDragWidth, setSectionDragWidth] = useState<number | null>(null)

  // Random rotation angle (±1°–2°) applied to the drag overlay for a playful lift effect
  const [dragRotation, setDragRotation] = useState<number | null>(null)

  // Track previous cumulative delta.y for frame-to-frame velocity during drag
  const prevDeltaYRef = useRef(0)

  // Composed drag handlers — route section drags to useSectionDrag, others to useTaskDragAndDrop
  const composedDragStart = useCallback((event: DragStartEvent) => {
    const angle = (Math.random() + 1) * (Math.random() < 0.5 ? -1 : 1)
    setDragRotation(angle)
    prevDeltaYRef.current = 0

    if (handleSectionDragStart(event)) {
      // Measure the dragged element so the overlay matches its width
      const rect = event.active.rect.current?.initial
      if (rect) setSectionDragWidth(rect.width)
      return
    }
    handleDragStart(event)
  }, [handleSectionDragStart, handleDragStart])

  const composedDragOver = useCallback((event: DragOverEvent) => {
    if (String(event.active.id).startsWith(SECTION_ID_PREFIX)) handleSectionDragOver(event)
    else handleDragOver(event)
  }, [handleSectionDragOver, handleDragOver])

  // During drag: tilt the overlay based on vertical movement direction,
  // and continuously update gap direction for cross-segment drags.
  // Uses direct DOM manipulation for tilt to avoid React re-renders on every pointer move.
  // CSS transition on --drag-rotation (registered via @property) provides smooth interpolation.
  // Gap direction must be tracked here because onDragOver only fires when the
  // over-item changes — not when the pointer crosses the midpoint within the same item.
  const composedDragMove = useCallback((event: DragMoveEvent) => {
    const dy = event.delta.y - prevDeltaYRef.current
    prevDeltaYRef.current = event.delta.y
    const targetAngle = Math.max(-2, Math.min(2, dy * 0.15))
    const el = document.querySelector('.drag-overlay-lift') as HTMLElement | null
    if (el) el.style.setProperty('--drag-rotation', `${targetAngle}deg`)
    // Update gap direction for cross-segment task drags
    if (!String(event.active.id).startsWith(SECTION_ID_PREFIX)) {
      taskDragMove(event)
    }
  }, [taskDragMove])

  const composedDragEnd = useCallback((event: DragEndEvent) => {
    if (String(event.active.id).startsWith(SECTION_ID_PREFIX)) handleSectionDragEnd(event)
    else handleDragEnd(event)
    setSectionDragWidth(null)
    setDragRotation(null)
  }, [handleSectionDragEnd, handleDragEnd])

  const composedDragCancel = useCallback(() => {
    handleSectionDragCancel()
    handleDragCancel()
    setSectionDragWidth(null)
    setDragRotation(null)
  }, [handleSectionDragCancel, handleDragCancel])

  // Custom collision detection: section drags only hit section-gap zones,
  // task drags only hit task items (no section-gap zones).
  // For section drags we bypass closestCenter entirely because dnd-kit's cached
  // droppableRects (measured via ResizeObserver) don't reflect the CSS transforms
  // applied by Phase A/B animations. Instead, we read each drop zone's current
  // getBoundingClientRect() (which includes transforms) and find the one whose
  // center is closest to the pointer position.
  const customCollisionDetection = useCallback<CollisionDetection>((args) => {
    const id = String(args.active.id)
    if (id.startsWith(SECTION_ID_PREFIX)) {
      const pointer = args.pointerCoordinates
      if (!pointer) return []

      let closestId: string | number | null = null
      let closestDist = Infinity

      for (const container of args.droppableContainers) {
        const cId = String(container.id)
        if (!cId.startsWith(SECTION_GAP_PREFIX)) continue

        const node = container.node.current
        if (!node || node.offsetHeight === 0) continue

        const rect = node.getBoundingClientRect()
        const centerY = rect.top + rect.height / 2
        const dist = Math.abs(pointer.y - centerY)

        if (dist < closestDist) {
          closestDist = dist
          closestId = container.id
        }
      }

      if (closestId !== null) {
        return [{ id: closestId, data: { value: closestDist } }]
      }
      return []
    }
    // For task drags, use pointer coordinates as the collision origin when the
    // source task has collapsed (cross-segment drag). Detect collapse by comparing
    // the active item's current measured height to its initial height — this avoids
    // depending on React state which can cause re-render oscillation (infinite loop).
    const pointer = args.pointerCoordinates
    const taskContainers = args.droppableContainers.filter(
      c => !String(c.id).startsWith(SECTION_GAP_PREFIX)
    )
    const initialH = args.active.rect.current.initial?.height ?? 0
    const currentH = args.active.rect.current.translated?.height ?? initialH
    const isCollapsed = initialH > 0 && currentH < initialH * 0.5

    if (pointer && isCollapsed) {
      // Use raw pointer position — no Y offset. A small offset previously
      // (-20px) aligned the gap with the overlay center but shifted collision
      // by one item, making certain drop positions unreachable from below.
      return closestCenter({
        ...args,
        collisionRect: {
          ...args.collisionRect,
          top: pointer.y,
          left: pointer.x,
          bottom: pointer.y,
          right: pointer.x,
          width: 0,
          height: 0,
        },
        droppableContainers: taskContainers,
      })
    }
    return closestCenter({
      ...args,
      droppableContainers: taskContainers,
    })
  }, [])

  // All sortable IDs across segments in visual order — single SortableContext
  // ensures cross-segment drag-and-drop shows correct displacement animations.
  const allSortableIds = useMemo(() => {
    const ids: string[] = []
    for (const seg of segments) {
      for (const t of seg.tasks) {
        if (t.id !== excludeId) ids.push(t.id)
      }
    }
    return ids
  }, [segments, excludeId])

  // Segment boundaries: [startIndex, endIndex] per segment in allSortableIds.
  // Used by the custom sorting strategy to restrict displacement to the target segment.
  const segmentBoundaries = useMemo(() => {
    const bounds: [number, number][] = []
    let offset = 0
    for (const seg of segments) {
      const count = seg.tasks.filter(t => t.id !== excludeId).length
      if (count > 0) {
        bounds.push([offset, offset + count - 1])
      }
      offset += count
    }
    return bounds
  }, [segments, excludeId])

  // Segment-aware sorting strategy:
  // - Same segment: standard verticalListSortingStrategy (items shift up/down normally)
  // - Cross segment: gap direction (before/after over-item) is determined dynamically
  //   by crossSegGapAfterRef based on the pointer's position relative to the over-item midpoint.
  //   When gapAfter=true: items AFTER overIndex shift down (gap opens after over-item).
  //   When gapAfter=false: items AT/AFTER overIndex shift down (gap opens before over-item).
  const segmentAwareStrategy = useMemo(
    () => {
      const findSeg = (idx: number) => segmentBoundaries.findIndex(([s, e]) => idx >= s && idx <= e)
      return ((args: Parameters<typeof verticalListSortingStrategy>[0]) => {
        const { activeIndex, overIndex, index, activeNodeRect } = args
        const activeSeg = findSeg(activeIndex)
        const overSeg = findSeg(overIndex)
        const itemSeg = findSeg(index)
        const noMove = { x: 0, y: 0, scaleX: 1, scaleY: 1 }

        // Same segment: standard displacement, but only for items in that segment
        if (activeSeg === overSeg) {
          if (itemSeg !== activeSeg) return noMove
          return verticalListSortingStrategy(args)
        }

        // Cross-segment: only affect items in the target segment
        if (overSeg === -1 || itemSeg !== overSeg) return noMove

        // Use the over item's height as reference — activeNodeRect may be 0 because
        // the active item collapses in the source segment during cross-segment drag.
        const refHeight = args.rects[overIndex]?.height ?? activeNodeRect?.height ?? 0
        const shift = { x: 0, y: refHeight + 2, scaleX: 1, scaleY: 1 }

        // Gap direction from pointer position (with hysteresis, set in handleDragOver)
        const gapAfter = crossSegGapAfterRef.current ?? true

        if (gapAfter) {
          // Gap AFTER over-item: shift items after overIndex
          return index > overIndex ? shift : noMove
        } else {
          // Gap BEFORE over-item: shift items at/after overIndex
          return index >= overIndex ? shift : noMove
        }
      }) as typeof verticalListSortingStrategy
    },
    [segmentBoundaries, crossSegGapAfter],
  )

  // Source segment index of the dragged section — used to hide adjacent drop zones
  const dragSourceSegIdx = activeSectionDragId
    ? segments.findIndex(s => s.section?.id === activeSectionDragId)
    : -1

  return {
    composedDragStart,
    composedDragMove,
    composedDragOver,
    composedDragEnd,
    composedDragCancel,
    customCollisionDetection,
    allSortableIds,
    segmentAwareStrategy,
    sectionDragWidth,
    dragRotation,
    dragSourceSegIdx,
  }
}
